//@flow
import { networkWrap } from './Network.js';
import {EventEmitter} from "eventemitter3";
import {useMutation, useQuery, useQueryClient} from "react-query";
import {UseQueryResult, UseMutationResult, UseMutationOptions} from "react-query";
import Notifications from "../components/Notifications";
import * as Utils from "../commons/Utils";

export type TableState<T> = {
  data?: T[],
  loading: boolean,
  pageCount: number,
  pageSize: number,
  pageIndex: number
}

export type WebCredentials = {
  user: string,
  password: string,
  rememberMe: boolean
}

export type UserWebInfoBasic = {
  sessionId: string,
  label: string,
  admin: boolean,
  email?: string,
  needsConsents: boolean,
  structure: string
}
export type AdditionalData = {
  key: string,
  value?: string
}

export type LocationWebInfo = {
  id: number,
  name: string,
  path: string,
  label: string,
  parent?: LocationWebInfo
}

export type PrinterEditable = {
  id?: number,
  name?: string,
  manualInactive?: boolean,
  nis: string,
//  clientId?: string,
  location?: string,
  lwi?: LocationWebInfo,
  description?: string,
  additionalData?: AdditionalData[]
}
export type PrinterWebInfo = PrinterEditable & {
  name: string,
  id: number,
  macNumber: string,
  serialNumber: string,
  totalCounter?: number,
  monoCounter?: number,
  colorCounter?: number,
  scanBWCounter?: number,
  scanColorCounter?: number,
  manualCounter?: boolean,
  errorStatess?: string[],
  tonerCyan?: number,
  tonerMagenta?: number,
  toneryYellow?: nubmer,
  tonerBlack?: number,
  alerts?: string[],
  serviceCauses?: string[],
  jamLocations?: string[],
  offlineCauses?: string[],
  lastContacted?: string,
  lastContactedRaw?: number,
  lastContactedAgo?: number,
  lastCountersUpdate?: string,
  lastCountersUpdateRaw?: number,
  lastAlertsUpdate?: string,
  lastAlertsUpdateRaw?: number
}

export type TableResult<T> = {
  data?: T[],
  totalCount: number
}

export type PrinterResult = TableResult<PrinterWebInfo>;

export type LocationResult = TableResult<LocationWebInfo>;

export type LocationAddEditRequest = {
  id?: number,
  name: string,
  parent?: LocationWebInfo
}

export type TableSortRequest = {
  id: string,
  desc: boolean,
  baseSort: boolean
}

export type DataProviderReq = {
  pageSize: number,
  pageIndex: number,
  query: string,
  sort: TableSortRequest
}

export type TableQuery = {
  /** skip **/
  offset: number,
  /** limit **/
  count: number,
  /** szukane słowo **/
  filter: string,
  /** kolumna po której sortujemy **/
  sorting: string,
  ascending: boolean
}

export type PrinterHistoryEntry = {
  data: string,
  dateRaw: number,
  errorStates?: string[],
  jamLocations?: string[],
  serviceCauses?: string[],
  offlineCauses?: string[]
}

export type PrinterHistoryResult = {
  history?: PrinterHistoryEntry[],
  /** Data najnowszego zdarzenia strony - kursor do doczytania nowszych wpisów */
  first: number,
  /** Data najstarszego zdarzenia strony - kursor do doczytania starszych wpisów */
  last: number,
  /** Czy w bazie są jeszcze starsze zdarzenia */
  more: boolean
}

export type PrinterConsumablesQuery = {
  printerId: number
}

export type PrinterConsumablesEntry = {
  material: string,
  color: string,
  event: string,
  /** null gdy nie udało się ustalić początku stanu */
  openedAt?: string,
  closedAt: string,
  closedAtRaw: number,
  durationMinutes?: number,
  mainCounter: number,
  /** liczniki z odczytu najbliższego wymianie - z jednego odczytu, więc mono + kolor = total */
  totalCounter?: number,
  monoCounter?: number,
  colorCounter?: number,
  /** czas odczytu liczników; null gdy dla tej wymiany nie ma odczytu */
  countersAt?: string,
  notes?: string
}

export type PrinterConsumablesResult = {
  consumables?: PrinterConsumablesEntry[],
  /** true gdy historia przekroczyła limit i najstarsze wymiany mogły zostać pominięte */
  truncated: boolean,
  /** zakres, za jaki policzono wymiany */
  months: number
}

export type PasswordChangeRequest = {
  id?: number,
  oldPassword: string,
  newPassword: string
}

export type ViewRange = {
  type: string,
  removed: boolean,
  observer: string[]
}

export type NotificationSettings = {
  printerId: number,
  notificationKeys?: string[],
  /** tylko ustawienia globalne: 'new' = tylko nowe zdarzenia, 'all' = wszystkie aktywne alerty drukarki */
  notifyMode?: 'new' | 'all'
}

export type LocationDeleteRequest = {
  lwi: LocationWebInfo,
  firstRun: boolean
}

export type GSQueryResult<T> = UseQueryResult<T, any> & {
  key: string,
  invalidate: ()=>void
}

export type ReportParamType = 'date' | 'location' | 'printer';

export type ReportParam = {
    id: string,
    name: string;
    type: ReportParamType;

}

export type ReportInfo = {
  id: string;
  name: string;
  params: ReportParam[];
}

export type ReportData = {
  reports: ReportInfo[];
}

export type ReportParamValue = {
  id: string,
  value: any;
}

export type ReportRequest = {
  reportId: string,
  params: ReportParamValue[]
}

export type ReportResponse = {
  name: string;
  //base64 enc report
  data: string;
}

export type ReportDistributionInfo =  {
  id: number | null;
  name: string;
  cron: string;
  emails: string;
  reportId: string;
  params: {
    range?: number;
    locationId?: any;
    printerId?: any;
  };
}

export type PrinterLookupInfo = {
  id: number,
  label: string,
  name?: string,
  serialNumber?: string,
  nis?: string,
  location?: string,
}

export type PrinterLookupResult = TableResult<PrinterLookupInfo>;


export type ReportDistributionResult = TableResult<ReportDistributionInfo>;

/** Dane użytkownika widziane przez administratora organizacji */
export type UserAdminInfo = {
  id?: number,
  username: string,
  firstName: string,
  lastName: string,
  email: string,
  admin: boolean
}

export type UserAdminResult = TableResult<UserAdminInfo>;

/** Odpowiedź strony ustawiania hasła na sprawdzenie tokena z linku */
export type PasswordTokenInfo = {
  valid: boolean,
  login?: string
}

export type PasswordTokenRequest = {
  token: string,
  password: string
}

class _DataAPI {
  getPrinterDetails(id: number): Promise<PrinterWebInfo> {}
  getPrinters(req: TableQuery): Promise<PrinterResult> {}
  getLocationList(req: TableQuery): Promise<LocationResult> {}

  getLocation(id: number): Promise<LocationWebInfo> {}
  getUserWebInfo(): Promise<UserWebInfoBasic> {}
  getNotifications(printerId: number): Promise<NotificationSettings> {}
  getPrinterConsumables(req: PrinterConsumablesQuery): Promise<PrinterConsumablesResult> {}

  getReports(): Promise<ReportData> {}
  getReport(req: ReportRequest): Promise<ReportResponse> {}

  getReportDistributionList(req: TableQuery): Promise<ReportDistributionResult> {}
  deleteDistribution(id: number): Promise<void> {}

  getDistribution(id: number): Promise<ReportDistributionInfo> {}

  getUserList(req: TableQuery): Promise<UserAdminResult> {}
  getManagedUser(id: number): Promise<UserAdminInfo> {}
}

class _MutationAPI {
  addEditLocation(location: LocationWebInfo): Promise<LocationWebInfo> {}
  deletePrinter(id: number): Promise<void> {}
  deleteLocation(req: LocationDeleteRequest): Promise<string> {}
  deleteDistribution(id: number): Promise<void> {}
  runDistributionNow(id: number): Promise<void> {}

  updatePrinter(pwi: PrinterWebInfo): Promise<void> {}
  saveEmail(email: string): Promise<UserWebInfoBasic> {}
  saveNotifications(settings: NotificationSettings): Promise<void> {}
  changePassword(password: PasswordChangeRequest): Promise<void> {}

  addEditDistribution(req: ReportDistributionInfo): Promise<void> {}

  addEditUser(req: UserAdminInfo): Promise<void> {}
  deleteUser(id: number): Promise<void> {}
  resetUserPassword(id: number): Promise<void> {}
}

export type MutationParams = {
  before?: ()=>{},
  afterSuccess?: (data: any)=>{},
  mutationOpts?: {},
  customMsg?: string,
  notifyExecute: boolean
}

export const MutationAPI: _MutationAPI = new _MutationAPI();
/**
 * obiekt wykonujący zapytania przez webscockety
 * @type {_DataAPI}
 */
export const DataAPI: _DataAPI = new _DataAPI();

export const useMutator = <P, R, P, TError, TVariables, TContext>(method: (data: P)=>Promise<R> , params: MutationParams): UseMutationResult<P, TError, TVariables, TContext> => {
  const options: UseMutationOptions<P> = {
    onError: (error, variables, context) => {
      console.log('error: ', error)
      Notifications.pushNew(error, 'danger');
    },
    onSuccess: (data, variables, context) => {
      if (Utils.isUndef(params.notifyExecute) || params.notifyExecute) {
        Notifications.pushNew(params.customMsg ? params.customMsg : 'Akcja wykonana', 'info');
      }
      if (Utils.isFun(params.afterSuccess)) params.afterSuccess(data);
    },

    onMutate: (vars) => {
    }
  };
  return useMutation((d: P)=>method(d), options);
}

export type Keys = 'printer' | 'location' | 'location-list' | 'distribution' | 'reportdistribution' | 'printer-notifications' | 'user' | 'user-list' | 'managed-user';

export const useData = <P, R>(param: P, key: Keys, method: (param: P)=>Promise<R>, options): GSQueryResult<R> => {
    const queryClient = useQueryClient();
    const paramKey = !options || Utils.isUndef(options.useParamInKey) ? true : options.useParamInKey;
    const queryKey = paramKey ? [key, param] : key;
    // console.log('getting data for: ' + JSON.stringify(queryKey))
    const queryObj = useQuery(queryKey, ()=> method(param), options);
    queryObj.key = queryKey;
    //wszystkie po kluczu
    queryObj.invalidate = ()=>{
      // console.log('invalidating: ' + JSON.stringify(key))
      queryClient.invalidateQueries(key);
    }
    return queryObj;
}

/**
 * zapytania, które nie są ani mutatorami ani cacheowanymi query
 */
class _API {
  authenticate(credentials: WebCredentials): Promise<UserWebInfoBasic> {}
  signout(): Promise<void>  {}
  acceptRules(): Promise<void>  {}

  getPrinterHistory(id: number, from: number, count: number): Promise<PrinterHistoryResult>  {}
  getLocations(query: string): Promise<LocationResult> {}
  getPrintersByQuery(query: string): Promise<PrinterLookupResult> {}

  //publiczne - strona ustawiania hasła działa bez zalogowania
  getPasswordTokenInfo(token: string): Promise<PasswordTokenInfo> {}
  setPasswordWithToken(req: PasswordTokenRequest): Promise<void> {}
}

export type Handler = {
  unregister: ()=>void
}

export class DbHandler {
  unregister: ()=>void;
  constructor(handler, unregister) {
    this.handler = handler;
    this.unregister = unregister;
  }
}

class _ClientAPI {
  constructor() {
    this.handlers = {};
  }

  /**
   tą metodę wywołuje tylko klient, powinna być gdzieś indziej
   **/
  registerHandler(type, handler) {
    let h = this.handlers[type];
    if (!h) h = this.handlers[type] = [];
    let dbh = new DbHandler(handler, ()=> {
      let idx =  h.indexOf(dbh);
      if (idx > -1) h.splice(idx, 1);
    });
    h.push(dbh);
    return dbh;
  }

  dbChange(change) {
    let id = change.id;
    let objType = change.objType;
    let changeType = change.changeType;
    let toInvoke = this.handlers[objType];
    //console.log('got: ', change, ' - found: ', toInvoke ? toInvoke.length : '[none]', ' handlers for type:', objType);
    if (toInvoke) toInvoke.forEach((h, idx)=>{
      h.handler(changeType, id);
    });
  }
}


const API = new _API();
const ClientAPI = new _ClientAPI();

const server=process.env.REACT_APP_SERVER;
let url;
if((typeof(server)==='string') && server.length>0) {    // tryb developerski
  url="ws://"+process.env.REACT_APP_SERVER.substr(7)+"/ws/data"; // zakładamy że zawsze jest http://
} else {
  url=((window.location.protocol==='https:')?'wss':'ws')
      +"://"
      +window.location.hostname+
      (window.location.port>0?":"+window.location.port:"")
      +"/ws/data";
}

let ns = networkWrap([API, DataAPI, MutationAPI], ClientAPI, url);
ns.connect();

API.clientApi = ClientAPI;

export default API;

export const emitter = new EventEmitter();
